import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Trending search chips — shown in the Home empty state below
 * "Try searching for…". Reads /api/marketplace/trending-searches, which
 * aggregates the search_queries_log collection populated by the alias
 * tracker. Backend filters out spam (<3 chars, numeric-only, single-hit).
 *
 *   - Hides entirely when the backend returns no trending queries.
 *   - Mobile-first row of chips with horizontal scroll if overflow.
 *   - EN/AR via the query text itself; the heading uses i18n.
 *
 * Props:
 *   onSelect(query): fired when the user taps a chip.
 *   limit: max chips to fetch (default 8).
 */
export default function TrendingSearchesChips({ onSelect, limit = 8 }) {
  const { t, i18n } = useTranslation();
  const isRTL = (i18n.resolvedLanguage || i18n.language || '').startsWith('ar');
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/marketplace/trending-searches?limit=${limit}`
        );
        if (!res.ok) return;
        const json = await res.json();
        if (mounted) {
          setItems(Array.isArray(json.trending) ? json.trending : []);
          setLoaded(true);
        }
      } catch {
        if (mounted) setLoaded(true);
      }
    })();
    return () => { mounted = false; };
  }, [limit]);

  // Hide entirely until loaded AND there's something to show.
  if (!loaded || items.length === 0) return null;

  return (
    <div className="trending-chips" dir={isRTL ? 'rtl' : 'ltr'} data-testid="trending-searches-chips">
      <h4 className="trending-chips-title" data-testid="trending-searches-title">
        {isRTL ? 'الأكثر بحثاً الآن' : 'Trending now'}
      </h4>
      <div className="trending-chips-row">
        {items.map((it, i) => (
          <button
            key={`${it.query}-${i}`}
            type="button"
            className="trending-chip"
            onClick={() => onSelect && onSelect(it.query)}
            data-testid={`trending-chip-${i}`}
            aria-label={`${isRTL ? 'بحث عن' : 'Search for'} ${it.query}`}
          >
            <span className="trending-chip-text">{it.query}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
